import React, { useState } from "react";
import { ExternalLink, Building2, MapPin, ShieldCheck, ShieldAlert, Info } from "lucide-react";
import { Button } from "./ui/button";

const CRED = {
  High: { cls: "text-emerald-700 border-emerald-200 bg-emerald-50", Icon: ShieldCheck },
  Medium: { cls: "text-amber-700 border-amber-200 bg-amber-50", Icon: ShieldAlert },
  Low: { cls: "text-slate-500 border-slate-300 bg-slate-100", Icon: Info },
};

const inWindow = (job, window) => {
  if (!window || !job.salary_min) return false;
  return job.salary_max >= window.low && job.salary_min <= window.high;
};

const paysMore = (job, window) => !!window && !!job.salary_min && job.salary_min > window.high;

const JobCard = ({ job, roleId, onCreate, generatingId }) => {
  const cred = job.credibility || { label: "Medium", score: 50, reasons: [], india_eligibility: "unstated" };
  const { cls, Icon } = CRED[cred.label] || CRED.Medium;
  const overseasRemote = job.work_model === "remote" && job.region !== "india";
  return (
    <article className="rounded-lg border border-slate-200 bg-white p-3 text-xs space-y-1.5" data-testid={`job-listing-${roleId}-${job.id}`}>
      <div className="flex items-start justify-between gap-2">
        <p className="text-emerald-700 flex gap-1.5 min-w-0"><Building2 className="w-3.5 h-3.5 shrink-0 mt-0.5" /><span className="break-words">{job.company}</span></p>
        <span className={`shrink-0 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] ${cls}`} title={(cred.reasons || []).join(" · ")} data-testid={`job-credibility-${roleId}-${job.id}`}>
          <Icon className="w-3 h-3" /> {cred.label} credibility
        </span>
      </div>
      <a href={job.url} target="_blank" rel="noopener noreferrer" className="text-slate-900 font-medium hover:text-sky-700 inline-flex gap-1 leading-relaxed" data-testid={`job-link-${roleId}-${job.id}`}>{job.title}<ExternalLink className="w-3 h-3 shrink-0 mt-1" /></a>
      <p className="text-slate-500 flex gap-1.5"><MapPin className="w-3 h-3 shrink-0 mt-0.5" />{job.location}</p>
      <p className="text-slate-500 capitalize">{job.work_model === "unspecified" ? "Work model not specified" : job.work_model} · {job.source}{job.link_status === "live" ? " · link verified" : ""}</p>
      <p className="text-slate-500" data-testid={`job-salary-${roleId}-${job.id}`}>{job.salary || "Salary not disclosed"}</p>
      {!!(cred.reasons || []).length && <p className="text-[10px] text-slate-500" data-testid={`job-credibility-reason-${roleId}-${job.id}`}>{cred.reasons[0]}</p>}
      {overseasRemote && cred.india_eligibility !== "stated" && (
        <p className="text-[10px] text-amber-700" data-testid={`job-eligibility-${roleId}-${job.id}`}>India eligibility not stated — confirm on the posting before applying.</p>
      )}
      <p className="text-[10px] text-slate-500" data-testid={`job-posted-${roleId}-${job.id}`}>
        {typeof job.age_days === "number" ? `Posted ${job.age_days === 0 ? "today" : `${job.age_days} day${job.age_days === 1 ? "" : "s"} ago`} · ` : ""}feed checked {new Date(job.fetched_at).toLocaleString()}
      </p>
      <Button size="sm" variant="outline" disabled={!!generatingId} onClick={() => onCreate(job)} className="w-full border-slate-300 text-sky-700 text-xs h-auto py-2 whitespace-normal" data-testid={`tailor-job-${roleId}-${job.id}`}>
        {generatingId === job.id ? "Generating…" : "Tailor to this vacancy"}
      </Button>
    </article>
  );
};

export default function JobListings({ jobs, roleId, loading, error, onCreate, generatingId, salaryWindow }) {
  const [expanded, setExpanded] = useState(false);
  const [showOther, setShowOther] = useState(false);
  const [anyRange, setAnyRange] = useState(null);

  if (loading) return <p className="text-xs text-slate-500 py-3" data-testid={`jobs-loading-${roleId}`}>Checking company career boards…</p>;
  if (error) return <p className="text-xs text-amber-700 py-3" data-testid={`jobs-error-${roleId}`}>{error}</p>;
  if (!jobs?.length) return <p className="text-xs text-slate-500 py-3" data-testid={`jobs-empty-${roleId}`}>No matching vacancies in the connected feeds for these filters. This role is a profile match, not a confirmed opening.</p>;

  const inRange = salaryWindow ? jobs.filter((j) => inWindow(j, salaryWindow)) : jobs;
  const undisclosed = salaryWindow ? jobs.filter((j) => !j.salary_min) : [];
  const levelOne = salaryWindow ? [...inRange, ...undisclosed] : jobs;
  const levelTwo = jobs.filter((j) => !levelOne.includes(j));
  const higher = levelTwo.filter((j) => paysMore(j, salaryWindow));
  const noHigher = !!salaryWindow && higher.length === 0;
  const otherOpen = showOther;
  const shown = levelOne.slice(0, expanded ? 6 : 2);

  return (
    <div className="space-y-3 mt-3 border-t border-slate-200 pt-3" data-testid={`jobs-list-${roleId}`}>
      {levelOne.length === 0 && salaryWindow ? (
        <p className="text-xs text-slate-500" data-testid={`jobs-none-in-range-${roleId}`}>No live vacancy here matches your expected range right now.</p>
      ) : (
        shown.map((job) => <JobCard key={job.id} job={job} roleId={roleId} onCreate={onCreate} generatingId={generatingId} />)
      )}
      {levelOne.length > 2 && (
        <button className="text-xs text-sky-700" onClick={() => setExpanded(!expanded)} data-testid={`more-jobs-${roleId}`}>
          {expanded ? "Show fewer vacancies" : `Show ${levelOne.length - 2} more vacancies`}
        </button>
      )}

      {salaryWindow && levelTwo.length > 0 && (
        <div data-testid={`jobs-level-two-${roleId}`}>
          {noHigher && anyRange === null ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-2.5" data-testid={`jobs-no-higher-prompt-${roleId}`}>
              <p className="text-[11px] text-slate-700">No higher salary range for this position is available in our website or partner websites. Do you want to check the job availability at any salary range?</p>
              <div className="mt-2 flex gap-2">
                <button onClick={() => { setAnyRange("yes"); setShowOther(true); }} className="h-8 rounded-lg bg-sky-600 px-3 text-[11px] font-semibold text-white hover:bg-sky-700" data-testid={`jobs-any-range-yes-${roleId}`}>Yes</button>
                <button onClick={() => setAnyRange("no")} className="h-8 rounded-lg border border-slate-200 bg-white px-3 text-[11px] font-semibold text-slate-700" data-testid={`jobs-any-range-no-${roleId}`}>No</button>
              </div>
            </div>
          ) : anyRange === "no" ? (
            <button onClick={() => setAnyRange(null)} className="text-xs text-sky-700" data-testid={`jobs-any-range-reopen-${roleId}`}>Check vacancies at any salary range</button>
          ) : (
            <>
              <button onClick={() => setShowOther((v) => !v)} className="text-xs text-sky-700" data-testid={`jobs-level-two-toggle-${roleId}`}>
                {otherOpen ? "Hide" : "Show"} {higher.length > 0 ? `${higher.length} higher-paying vacanc${higher.length === 1 ? "y" : "ies"}` : `${levelTwo.length} vacanc${levelTwo.length === 1 ? "y" : "ies"} at other ranges`}
              </button>
              {otherOpen && (
                <div className="mt-2 space-y-3" data-testid={`jobs-level-two-list-${roleId}`}>
                  {[...higher, ...levelTwo.filter((j) => !higher.includes(j))].slice(0, 6).map((job) => (
                    <JobCard key={job.id} job={job} roleId={roleId} onCreate={onCreate} generatingId={generatingId} />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
